import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import './TableSign.css';

const TableSign = () => {
  const navigate = useNavigate();
  const { isGuest } = useAuth();
  
  // 状态管理
  const [title, setTitle] = useState(''); // 台签标题
  const [names, setNames] = useState(''); // 输入的单位名称（每行一个）
  const [fontName, setFontName] = useState('华文新魏'); // 字体
  const [copiesPerName, setCopiesPerName] = useState(2); // 每个名称的份数
  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]); // 历史记录列表
  const [loadedHistoryId, setLoadedHistoryId] = useState(null); // 当前加载的历史记录ID

  // 根据当前访问地址自动判断 API 地址
  const API_BASE_URL = process.env.REACT_APP_API_URL || 
    (window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin);

  // 获取认证头
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // 检查登录状态
  const checkLogin = () => {
    if (isGuest) {
      navigate('/login');
      return false;
    }
    return true;
  };

  // 加载历史记录
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setHistoryList([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-sign/history`, {
        headers: getAuthHeaders()
      });
      setHistoryList(response.data.data || []);
    } catch (error) {
      console.error('加载历史记录失败:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      }
    }
  };

  // 加载历史记录到编辑区
  const handleLoadHistory = async (historyId) => {
    if (!window.confirm('加载历史记录将覆盖当前内容，确定继续？')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-sign/history/${historyId}`, {
        headers: getAuthHeaders()
      });
      
      const data = response.data.data;
      setTitle(data.title || '');
      setNames(data.names.join('\n'));
      setFontName(data.font_name || '华文新魏');
      setCopiesPerName(data.copies_per_name || 2);
      setLoadedHistoryId(historyId); // 记录加载的历史记录ID
      alert('历史记录加载成功');
    } catch (error) {
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      } else {
        alert(error.response?.data?.detail || '加载失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 删除历史记录
  const handleDeleteHistory = async (historyId) => {
    if (!window.confirm('确定要删除这条历史记录吗？')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/table-sign/history/${historyId}`, {
        headers: getAuthHeaders()
      });
      alert('历史记录删除成功');
      await loadHistory();
    } catch (error) {
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      } else {
        alert(error.response?.data?.detail || '删除失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 保存历史记录
  const saveHistory = async (nameList) => {
    try {
      let historyTitle = title;
      if (!historyTitle) {
        historyTitle = `台签_${new Date().toLocaleString('zh-CN', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).replace(/[\/\s:]/g, '-')}`;
      }
      
      if (loadedHistoryId) {
        // 更新已加载的历史记录
        await axios.put(`${API_BASE_URL}/api/table-sign/history/${loadedHistoryId}`, {
          title: historyTitle,
          names: nameList,
          font_name: fontName,
          copies_per_name: copiesPerName
        }, {
          headers: getAuthHeaders()
        });
        console.log('历史记录更新成功');
      } else {
        // 创建新的历史记录
        await axios.post(`${API_BASE_URL}/api/table-sign/save`, {
          title: historyTitle,
          names: nameList,
          font_name: fontName,
          copies_per_name: copiesPerName
        }, {
          headers: getAuthHeaders()
        });
        console.log('历史记录保存成功');
      }
      
      // 刷新历史记录列表
      await loadHistory();
    } catch (error) {
      console.error('保存历史记录失败:', error);
    }
  };

  // 解析名称列表（支持多种分隔符，忽略括号内的分隔符，保留连续两个空格用于两字人名）
  const parseNames = (text) => {
    if (!text || !text.trim()) return [];
    
    // 使用特殊占位符标记连续两个空格（用于两字人名）
    const DOUBLE_SPACE_MARKER = '___DOUBLE_SPACE___';
    
    // 1. 先将连续两个空格临时替换为占位符
    let processedText = text.replace(/  /g, DOUBLE_SPACE_MARKER);
    
    // 2. 标记括号内的位置
    const bracketPairs = {
      '（': '）', '(': ')',
      '【': '】', '[': ']',
      '｛': '｝', '{': '}',
      '〈': '〉', '<': '>',
    };
    const openBrackets = new Set(Object.keys(bracketPairs));
    const closeBrackets = new Set(Object.values(bracketPairs));
    
    const inBracket = new Array(processedText.length).fill(false);
    const stack = [];
    
    for (let i = 0; i < processedText.length; i++) {
      const char = processedText[i];
      if (openBrackets.has(char)) {
        stack.push(i);
      } else if (closeBrackets.has(char) && stack.length > 0) {
        const start = stack.pop();
        for (let j = start; j <= i; j++) {
          inBracket[j] = true;
        }
      }
    }
    
    // 3. 定义分隔符
    const separators = new Set(['\n', '、', '，', ',', '。', '；', ';', '\t', ' ', '|', '｜']);
    
    // 4. 遍历文本，遇到不在括号内的分隔符就分割
    const nameList = [];
    let current = '';
    
    for (let i = 0; i < processedText.length; i++) {
      const char = processedText[i];
      if (separators.has(char) && !inBracket[i]) {
        if (current.trim()) {
          nameList.push(current.trim());
        }
        current = '';
      } else {
        current += char;
      }
    }
    
    // 处理最后一个名称
    if (current.trim()) {
      nameList.push(current.trim());
    }
    
    // 5. 将占位符还原为连续两个空格
    return nameList.map(name => name.replace(new RegExp(DOUBLE_SPACE_MARKER, 'g'), '  '));
  };

  // 生成台签文档
  const handleGenerate = async () => {
    if (!checkLogin()) return;
    
    const nameList = parseNames(names);
    if (nameList.length === 0) {
      alert('请输入至少一个单位名称');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/table-sign/generate`,
        {
          names: nameList,
          font_name: fontName,
          copies_per_name: parseInt(copiesPerName)
        },
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );

      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // 从响应头获取文件名
      const contentDisposition = response.headers['content-disposition'];
      let filename = '台签.docx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // 保存历史记录
      await saveHistory(nameList);
      
      alert('台签排版成功！');
    } catch (error) {
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      } else {
        alert(error.response?.data?.detail || '生成失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 快速填充示例数据
  const fillExampleData = () => {
    const examples = [
      '百色市大数据发展局',
      '市委办公室',
      '市政府办公室',
      '市人大常委会办公室',
      '市政协办公室',
      '市纪委监委',
      '市委组织部',
      '市委宣传部',
      '市委统战部',
      '市委政法委'
    ];
    setNames(examples.join('\n'));
  };

  // 清空数据
  const clearData = () => {
    setTitle('');
    setNames('');
    setLoadedHistoryId(null); // 清空历史记录ID
  };

  // 统计信息
  const nameCount = parseNames(names).length;
  const totalCount = nameCount * copiesPerName;

  return (
    <div className="table-sign-container">
      <div className="table-sign-content">
        {/* 左侧历史记录栏 */}
        <div className="history-panel">
          <div className="panel-header">
            <h3>📋 历史记录</h3>
          </div>
          <div className="history-list">
            {historyList.length === 0 ? (
              <div className="empty-history">暂无历史记录</div>
            ) : (
              historyList.map((item) => (
                <div key={item.id} className="history-item">
                  <div className="history-title-row">
                    <div className="history-title">{item.title}</div>
                    <div className="history-actions">
                      <button 
                        className="load-btn"
                        onClick={() => handleLoadHistory(item.id)}
                        disabled={!isGuest && loading}
                      >
                        加载
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteHistory(item.id)}
                        disabled={!isGuest && loading}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                  <div className="history-meta">
                    <span className="history-count">{JSON.parse(item.names).length} 个单位</span>
                    <span className="history-time">{item.updated_at || item.created_at}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 右侧输入区 */}
        <div className="input-panel">
          <div className="panel-header">
            <h3>📝 输入单位名称</h3>
            <div className="header-actions">
              <button className="action-btn secondary" onClick={fillExampleData}>
                填充示例
              </button>
              <button className="action-btn secondary" onClick={clearData}>
                清空
              </button>
            </div>
          </div>

          <div className="panel-body">
            {/* 标题输入框 */}
            <div className="form-group">
              <label>台签标题（可选）</label>
              <input
                type="text"
                className="title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="请输入台签标题，用于历史记录显示"
              />
            </div>

            <div className="form-group form-group-textarea">
              <label>单位名称（支持多种分隔符）</label>
              <textarea
                className="names-input"
                value={names}
                onChange={(e) => setNames(e.target.value)}
                placeholder="支持回车、顿号(、)、逗号等分隔&#10;两字人名请用两个空格间隔，如：张  三&#10;&#10;示例：&#10;百色市大数据发展局、市委办公室&#10;市政府办公室，市人大常委会办公室"
                rows={12}
              />
              <div className="input-stats">
                <span>共 {nameCount} 个单位</span>
                <span>生成 {totalCount} 个台签</span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>字体</label>
                <select 
                  className="font-select"
                  value={fontName}
                  onChange={(e) => setFontName(e.target.value)}
                >
                  <option value="华文新魏">华文新魏</option>
                  <option value="宋体">宋体</option>
                  <option value="黑体">黑体</option>
                  <option value="楷体">楷体</option>
                  <option value="仿宋">仿宋</option>
                </select>
              </div>

              <div className="form-group">
                <label>每个名称打印份数</label>
                <select 
                  className="copies-select"
                  value={copiesPerName}
                  onChange={(e) => setCopiesPerName(parseInt(e.target.value))}
                >
                  <option value={1}>1 份</option>
                  <option value={2}>2 份</option>
                  <option value={3}>3 份</option>
                  <option value={4}>4 份</option>
                  <option value={5}>5 份</option>
                </select>
              </div>
            </div>

            <div className="action-buttons">
              <button 
                className="action-btn success full-width"
                onClick={handleGenerate}
                disabled={!isGuest && (loading || nameCount === 0)}
              >
                {loading ? '生成中...' : '📥 生成文档'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableSign;
